import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";

type CategoryItem = {
  id?: string;
  name: string;
  slug: string;
  image?: string;
  image_url?: string | null;
};

const fallbackImage =
  "https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?q=80&w=900&auto=format&fit=crop";

const fallbackImagesBySlug: Record<string, string> = {
  blusas:
    "https://images.unsplash.com/photo-1551489186-cf8726f514f8?q=80&w=900&auto=format&fit=crop",
  "nova-colecao":
    "https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?q=80&w=900&auto=format&fit=crop",
  "saias-shorts":
    "https://images.unsplash.com/photo-1529139574466-a303027c1d8b?q=80&w=900&auto=format&fit=crop",
  calcas:
    "https://images.unsplash.com/photo-1594633312681-425c7b97ccd1?q=80&w=900&auto=format&fit=crop",
  blazer:
    "https://images.unsplash.com/photo-1591047139829-d91aecb6caea?q=80&w=900&auto=format&fit=crop",
  cropped:
    "https://images.unsplash.com/photo-1483985988355-763728e1935b?q=80&w=900&auto=format&fit=crop",
};

export default function CollectionBanner() {
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const autoRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      const { data, error } = await supabase
        .from("ray_melo_categories")
        .select("*")
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Erro ao buscar categorias:", error);
        setCategories([]);
        return;
      }

      setCategories(data || []);
    };

    fetchCategories();
  }, []);

  const getImage = (cat: CategoryItem) => {
    const dbImage = typeof cat.image_url === "string" ? cat.image_url.trim() : "";
    const localImage = typeof cat.image === "string" ? cat.image.trim() : "";

    return (
      dbImage ||
      localImage ||
      fallbackImagesBySlug[cat.slug] ||
      fallbackImage
    );
  };

  const scrollNext = () => {
    const el = scrollRef.current;
    if (!el) return;

    const cardWidth = el.clientWidth / 4;

    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 4) {
      el.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      el.scrollBy({ left: cardWidth, behavior: "smooth" });
    }
  };

  const scrollPrev = () => {
    const el = scrollRef.current;
    if (!el) return;

    const cardWidth = el.clientWidth / 4;

    if (el.scrollLeft <= 4) {
      el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    } else {
      el.scrollBy({ left: -cardWidth, behavior: "smooth" });
    }
  };

  const startAuto = () => {
    if (autoRef.current) clearInterval(autoRef.current);
    autoRef.current = setInterval(scrollNext, 2500);
  };

  const stopAuto = () => {
    if (autoRef.current) clearInterval(autoRef.current);
  };

  useEffect(() => {
    startAuto();

    return () => stopAuto();
  }, [categories]);

  if (categories.length === 0) return null;

  return (
    <section className="py-12 px-4 md:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <div className="flex-1 h-px bg-black/10" />
          <h2 className="text-[13px] tracking-[0.3em] font-semibold text-black uppercase">
            Categorias
          </h2>
          <div className="flex-1 h-px bg-black/10" />
        </div>

        <div className="relative">
          <button
            onClick={() => {
              stopAuto();
              scrollPrev();
              startAuto();
            }}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 flex items-center justify-center border border-black/20 bg-white hover:bg-black hover:text-white transition-all cursor-pointer"
            aria-label="Anterior"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>

          <button
            onClick={() => {
              stopAuto();
              scrollNext();
              startAuto();
            }}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 flex items-center justify-center border border-black/20 bg-white hover:bg-black hover:text-white transition-all cursor-pointer"
            aria-label="Próximo"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>

          <div
            ref={scrollRef}
            onMouseEnter={stopAuto}
            onMouseLeave={startAuto}
            className="grid grid-flow-col auto-cols-[calc(50%-4px)] md:auto-cols-[calc(25%-6px)] gap-2 overflow-x-auto snap-x snap-mandatory scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {categories.map((cat) => (
              <Link
                key={cat.id || cat.slug}
                to={`/categoria/${cat.slug}`}
                className="group relative overflow-hidden snap-start cursor-pointer"
                style={{ aspectRatio: "3/4" }}
              >
                <img
                  src={getImage(cat)}
                  alt={cat.name}
                  onError={(e) => {
                    e.currentTarget.src = fallbackImage;
                  }}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />

                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/45 transition-colors duration-300" />

                <div className="absolute inset-0 flex items-end justify-center pb-6">
                  <span className="text-white text-[13px] tracking-[0.2em] font-semibold uppercase">
                    {cat.name}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="text-center mt-8">
          <Link
            to="/catalogo"
            className="inline-block border border-black/30 px-8 py-3 text-[11px] tracking-[0.2em] uppercase hover:bg-black hover:text-white transition-all duration-300 whitespace-nowrap"
          >
            Ver Todas as Categorias
          </Link>
        </div>
      </div>
    </section>
  );
}